// ** React Imports
import { useState, useEffect, ChangeEvent } from 'react'

// ** Type Imports
import { ContloAnalyticsType } from 'src/lib/types/integrations'

// ** Child Imports
import Alerts from 'src/components/common/feedback-and-dialog/Alerts'
import { Switch, Grid, Typography, TextField } from '@mui/material'
import SaveNotification from 'src/components/common/SaveNotification'
import LayoutContainer from 'src/components/common/containers/LayoutContainer'
import IntegrationChannels from 'src/components/settings/IntegrationChannels'

// ** Context Imports
import { useIntegrations } from 'src/lib/contexts/IntegrationsContext'
import { useLoader } from 'src/lib/contexts/LoaderContext'

// ** Util Imports
import { getIntegrations, setAnalyticsConfig, verifyAnalyticsConfig } from 'src/lib/core/api-methods'
import { initialContloAnalyticsState } from 'src/lib/utils/integrationsHelpers'
import { alertSeverites } from 'src/lib/utils/constants'
import { useTemporary } from 'src/lib/contexts/TemporaryContext'
import useConfirmOnRouteChanges from 'src/lib/utils/custom-hooks/useConfirmOnRouteChanges'
import { useLocale } from 'src/lib/hooks/useLocal'
import Channels from '../channels/Channels'
import { getEnabledChannelsArray } from 'src/lib/utils/parsers'

interface ContloProps {
  isOnboarding?: boolean
  handleOnboardingSave?: () => void
}

const Contlo: React.FC<ContloProps> = ({ isOnboarding = false, handleOnboardingSave }) => {
  // Context hook
  const { setLoaderProperties } = useLoader()
  const { integrations, setIntegrations } = useIntegrations()
  const { temporary, setTemporaryData } = useTemporary()
  const { t } = useLocale()
  const { contlo } = integrations || {}

  //Local State
  const [contloAnalytics, setContloAnalytics] = useState<ContloAnalyticsType>(initialContloAnalyticsState)
  const [openAlert, setOpenAlert] = useState<string>('')
  const [alertMessage, setAlertMessage] = useState<string>('')
  const [settingsUpdated, setSettingsUpdated] = useState<boolean>(false)

  useEffect(() => {
    callGetAnalytics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!temporary) return
    setContloAnalytics(temporary.integrations?.contlo)
    setTemporaryData(undefined)
  }, [temporary, setTemporaryData])

  useEffect(() => {
    setSettingsUpdated(Boolean(JSON.stringify(contlo) !== JSON.stringify(contloAnalytics)))
  }, [contlo, contloAnalytics])

  useEffect(() => {
    if (!contlo) return
    setContloAnalytics(contlo)
    setTemporaryData(undefined)
    setSettingsUpdated(false)
  }, [contlo, setTemporaryData])

  useConfirmOnRouteChanges(settingsUpdated, () => {
    return new Promise((resolve: any, reject) => {
      if (confirm('You have unsaved changes. Do you want to stay on the page?')) {
        setTemporaryData({
          integrations: {
            ...integrations,
            contlo: contloAnalytics
          }
        })
        resolve(true)
      } else reject(false)
    })
  })

  const callGetAnalytics = async () => {
    try {
      const response: any = await getIntegrations()
      if (response?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, response?.error)
      } else {
        setIntegrations(response?.data)
      }
    } catch (error) {
      handleAlerts(alertSeverites.SEVERITY_ERROR, 'Could not fetch analytics configuration!')
    }
  }

  const isValidForm = () => {
    if (contloAnalytics?.enabled && !Boolean(contloAnalytics?.apiKey?.length)) {
      setOpenAlert('error')
      setAlertMessage('Please fill required contlo analytics fields!')
      return false
    }
    return true
  }

  const callSubmit = async () => {
    if (!isValidForm()) return
    const payload = {
      api_key: contloAnalytics?.apiKey?.trim(),
      enabled: contloAnalytics?.enabled,
      channels: getEnabledChannelsArray(contloAnalytics?.channels)
    }
    try {
      setLoaderProperties({ isLoaderVisible: true })
      const verifyResponse: any = await verifyAnalyticsConfig('contlo', payload)
      if (verifyResponse?.error) {
        setOpenAlert('error')
        setAlertMessage(verifyResponse?.error)
        console.error('x-trace-id:', verifyResponse.headers?.['x-trace-id'])
        setLoaderProperties({ isLoaderVisible: false })
        return
      }

      const response: any = await setAnalyticsConfig('contlo', payload)
      if (response?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, response?.error)
      } else {
        setIntegrations({ ...integrations, contlo: contloAnalytics }, false)
        setOpenAlert('success')
        setAlertMessage('Successfully saved analytics configuration!')
        handleOnboardingSave && handleOnboardingSave()
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errorMessage
        ? error?.response?.data?.errorMessage
        : `Could not save configuration!`
      handleAlerts(alertSeverites.SEVERITY_ERROR, errorMessage)
      console.error(error)
    }
    setLoaderProperties({ isLoaderVisible: false })
  }

  const handleDiscardSaving = () => {
    if (!contlo) return
    setContloAnalytics(contlo)
    setSettingsUpdated(false)
    setTemporaryData(undefined)
  }

  const handleAlerts = (severity: string, message?: string) => {
    setOpenAlert(severity)
    message && setAlertMessage(message)
  }

  const handleCloseAlert = (event: any, reason: any) => {
    if (reason === 'clickaway') {
      return
    }
    handleAlerts('')
  }

  return (
    <>
      <IntegrationChannels
        channels={contloAnalytics.channels ?? []}
        onChange={(channels: any) => {
          setContloAnalytics({ ...contloAnalytics, channels: channels })
        }}
        hideChannels={!contloAnalytics?.enabled}
      >
        <Grid display={'flex'} flexDirection={'column'} width={'100%'}>
          {/* <Typography variant='body1' sx={{ fontWeight: 600 }}>
                    Contlo analytics
                  </Typography> */}

          <Grid item xs={12} md={12} display={'flex'} flexDirection={'column'} justifyContent={'space-between'}>
            <Grid display={'flex'} alignItems={'center'} gap={36}>
              <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>
                API key:
              </Typography>
              <TextField
                fullWidth
                type='text'
                size='small'
                variant='outlined'
                sx={{ maxWidth: '400px' }}
                value={contloAnalytics?.apiKey}
                placeholder='API Key'
                inputProps={{ 'aria-label': 'controlled' }}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setContloAnalytics({ ...contloAnalytics, apiKey: event?.target?.value })
                }
              />
            </Grid>
          </Grid>

          <Grid item xs={12} md={12} display={'flex'} alignItems={'center'}>
            <Grid container>
              <Grid item xs={10} md={11} display={'flex'} alignItems={'center'}>
                <Typography variant='body2'>Contlo analytics is</Typography>
                <Typography variant='body1' sx={{ fontWeight: 500, marginLeft: 2 }}>
                  {`${contloAnalytics?.enabled ? 'enabled' : 'disabled'}`}
                </Typography>
              </Grid>
              <Grid item xs={2} md={1} display={'flex'} alignItems={'center'} justifyContent={'end'}>
                <Switch
                  name='contloToggle'
                  checked={contloAnalytics?.enabled}
                  inputProps={{ 'aria-label': 'controlled' }}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    const checked = event?.target?.checked
                    setContloAnalytics({ ...contloAnalytics, enabled: checked })
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </IntegrationChannels>
      {!Boolean(isOnboarding) ? (
        <SaveNotification onSave={callSubmit} onDiscard={handleDiscardSaving} isVisible={settingsUpdated} />
      ) : (
        <button
          className='px-3 py-2 ml-auto text-sm font-bold border rounded-lg w-fit text-[#FFFFFF] bg-[#01CCA7]'
          onClick={callSubmit}
        >
          {t('save')}
        </button>
      )}
      {openAlert.length ? (
        <Alerts
          open={true}
          duration={2000}
          closeHandler={handleCloseAlert}
          severity={openAlert}
          message={alertMessage}
        />
      ) : null}
    </>
  )
}
export default Contlo
